/**
 * Sandbox · detalle de corrida (v2.1).
 *
 * Header con name + status badge, tabs rounded-full chips
 * (Overview / Results / Diff / Logs). KPI cards rounded-xl en Overview,
 * tabla en Results, side-by-side en Diff, terminal mono en Logs.
 */
import { Link, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft, Check, X } from "lucide-react";
import { useSandboxResults, useSandboxRun } from "@/hooks/use-sandbox-run";
import { cn } from "@/lib/cn";
import type { SandboxResult, SandboxRun } from "@/types";

type Tab = "overview" | "results" | "diff" | "logs";

const TABS: Array<{ value: Tab; label: string }> = [
  { value: "overview", label: "Overview" },
  { value: "results", label: "Results" },
  { value: "diff", label: "Diff" },
  { value: "logs", label: "Logs" },
];

export function SandboxRunPage() {
  const { runId } = useParams({ strict: false }) as { runId: string };
  const { data: run } = useSandboxRun(runId);
  const { data: results } = useSandboxResults(runId);
  const [tab, setTab] = useState<Tab>("overview");

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="px-8 py-6 border-b border-border-subtle">
        <Link
          to="/sandbox"
          className="inline-flex items-center gap-1.5 text-2xs text-text-muted hover:text-text-primary mb-2"
        >
          <ArrowLeft size={12} strokeWidth={2} />
          Sandbox
        </Link>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-sans font-semibold text-2xl tracking-snug text-text-primary truncate">
                {run?.skillVersion ?? runId}
              </h1>
              {run && <StatusBadge status={run.status} />}
            </div>
            <div className="flex items-center gap-2 text-xs text-text-muted mt-1 flex-wrap">
              <span className="font-mono">{run?.fileName ?? "—"}</span>
              {run?.comparisonVersion && (
                <>
                  <span className="text-text-disabled">·</span>
                  <span>
                    vs{" "}
                    <span className="font-mono text-text-secondary">
                      {run.comparisonVersion}
                    </span>
                  </span>
                </>
              )}
              <span className="text-text-disabled">·</span>
              <span>por {run?.createdBy ?? "—"}</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mt-5">
          {TABS.map((t) => {
            const active = tab === t.value;
            return (
              <button
                key={t.value}
                onClick={() => setTab(t.value)}
                className={cn(
                  "h-8 px-3.5 rounded-full text-xs font-medium border transition-colors",
                  active
                    ? "bg-magenta-soft border-magenta-border text-magenta"
                    : "bg-surface-1 border-border text-text-secondary hover:border-border-strong hover:text-text-primary",
                )}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        {tab === "overview" && <OverviewTab run={run ?? null} results={results ?? []} />}
        {tab === "results" && <ResultsTab results={results ?? []} status={run?.status} />}
        {tab === "diff" && <DiffTab results={results ?? []} run={run ?? null} />}
        {tab === "logs" && <LogsTab run={run ?? null} />}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
 * Tabs
 * ──────────────────────────────────────────────────────────── */

function OverviewTab({
  run,
  results,
}: {
  run: SandboxRun | null;
  results: SandboxResult[];
}) {
  const matchRate = results.length
    ? Math.round(
        (results.filter((r) => r.pass === true).length / results.length) * 100,
      )
    : null;
  const avgLatency = "1.2s";
  const progress = run
    ? Math.round((run.processed / Math.max(run.totalQuestions, 1)) * 100)
    : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Total questions"
          value={run?.totalQuestions ?? "—"}
        />
        <KpiCard
          label="Processed"
          value={run?.processed ?? "—"}
          sub={
            run && run.status === "running"
              ? `${progress}%`
              : run?.status === "done"
                ? "100%"
                : undefined
          }
        />
        <KpiCard
          label="Match rate"
          value={matchRate !== null ? `${matchRate}%` : "—"}
          tone={
            matchRate === null
              ? "muted"
              : matchRate >= 90
                ? "success"
                : matchRate >= 75
                  ? "warning"
                  : "error"
          }
        />
        <KpiCard label="Avg latency" value={avgLatency} />
      </div>

      {/* Progress (running) */}
      {run?.status === "running" && (
        <div className="rounded-xl border border-border bg-surface-1 p-5 shadow-sm">
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-sm font-medium text-text-primary">
              En progreso
            </span>
            <span className="font-mono text-2xs tabular-nums text-text-muted">
              {run.processed}/{run.totalQuestions} · {progress}%
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-surface-3 overflow-hidden">
            <div
              className="h-1.5 rounded-full bg-magenta transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="rounded-xl border border-border bg-surface-1 p-5 shadow-sm">
        <h3 className="text-sm font-medium text-text-primary mb-3">
          Resumen
        </h3>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 text-sm">
          <Row label="Skill version" value={run?.skillVersion ?? "—"} mono />
          <Row label="Baseline" value={run?.comparisonVersion ?? "—"} mono />
          <Row label="Cliente" value={run?.client ?? "—"} mono />
          <Row label="Status" value={run?.status ?? "—"} />
          <Row label="Test set" value={run?.fileName ?? "—"} mono />
          <Row label="Triggered by" value={run?.createdBy ?? "—"} />
          <Row
            label="Created"
            value={run?.createdAt ? new Date(run.createdAt).toLocaleString() : "—"}
            mono
          />
          <Row
            label="Finished"
            value={
              run?.finishedAt
                ? new Date(run.finishedAt).toLocaleString()
                : run?.status === "running"
                  ? "En curso"
                  : "—"
            }
            mono
          />
        </dl>
      </div>
    </div>
  );
}

function ResultsTab({
  results,
  status,
}: {
  results: SandboxResult[];
  status?: SandboxRun["status"];
}) {
  if (!results.length) {
    return (
      <div className="rounded-xl border border-border bg-surface-1 shadow-sm px-8 py-16 text-center">
        <div className="text-sm text-text-muted">
          {status === "queued"
            ? "Corrida en cola — los resultados aparecerán acá"
            : "Resultados aparecen acá conforme se procesan"}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-surface-1 shadow-sm overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-surface-2 border-b border-border-subtle">
            <th className="text-left px-4 py-3 text-2xs uppercase tracking-wider text-text-muted font-medium w-10">
              #
            </th>
            <th className="text-left px-4 py-3 text-2xs uppercase tracking-wider text-text-muted font-medium">
              Question
            </th>
            <th className="text-left px-4 py-3 text-2xs uppercase tracking-wider text-text-muted font-medium">
              Expected
            </th>
            <th className="text-left px-4 py-3 text-2xs uppercase tracking-wider text-text-muted font-medium">
              Actual
            </th>
            <th className="text-left px-4 py-3 text-2xs uppercase tracking-wider text-text-muted font-medium w-28">
              Match
            </th>
          </tr>
        </thead>
        <tbody>
          {results.map((r, i) => (
            <tr
              key={r.id}
              className={cn(
                i !== results.length - 1 && "border-b border-border-subtle",
              )}
            >
              <td className="px-4 py-3 align-top font-mono tabular-nums text-text-muted">
                {String(i + 1).padStart(2, "0")}
              </td>
              <td className="px-4 py-3 align-top text-text-primary max-w-[280px]">
                {r.question}
              </td>
              <td className="px-4 py-3 align-top text-text-secondary max-w-[260px]">
                {r.expectedAnswer ?? "—"}
              </td>
              <td className="px-4 py-3 align-top text-text-secondary max-w-[260px]">
                {r.actualAnswer}
              </td>
              <td className="px-4 py-3 align-top">
                <MatchPill pass={r.pass} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DiffTab({
  results,
  run,
}: {
  results: SandboxResult[];
  run: SandboxRun | null;
}) {
  if (!results.length || !run?.comparisonVersion) {
    return (
      <div className="rounded-xl border border-border bg-surface-1 shadow-sm px-8 py-16 text-center">
        <div className="text-sm text-text-muted">
          {!run?.comparisonVersion
            ? "Esta corrida no tiene baseline para comparar."
            : "Esperando resultados para generar diff."}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {results.map((r, i) => (
        <article
          key={r.id}
          className="rounded-xl border border-border bg-surface-1 shadow-sm overflow-hidden"
        >
          <div className="px-5 py-3 border-b border-border-subtle flex items-baseline justify-between gap-3">
            <div className="min-w-0">
              <div className="font-mono text-2xs tabular-nums text-text-muted">
                #{String(i + 1).padStart(2, "0")}
              </div>
              <div className="text-sm text-text-primary mt-0.5 truncate">
                {r.question}
              </div>
            </div>
            <MatchPill pass={r.pass} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border-subtle">
            <div className="p-4">
              <div className="text-2xs uppercase tracking-wider text-text-muted mb-1.5">
                Baseline ·{" "}
                <span className="font-mono normal-case tracking-normal">
                  {run.comparisonVersion}
                </span>
              </div>
              <p className="text-sm text-text-secondary">
                {r.comparisonAnswer ?? "—"}
              </p>
            </div>
            <div className="p-4 bg-magenta-soft/30">
              <div className="text-2xs uppercase tracking-wider text-magenta mb-1.5">
                Candidate ·{" "}
                <span className="font-mono normal-case tracking-normal">
                  {run.skillVersion}
                </span>
              </div>
              <p className="text-sm text-text-primary">{r.actualAnswer}</p>
            </div>
          </div>
          {r.diffSummary && (
            <div className="px-5 py-2.5 border-t border-border-subtle bg-surface-2 text-2xs text-text-secondary">
              {r.diffSummary}
            </div>
          )}
        </article>
      ))}
    </div>
  );
}

function LogsTab({ run }: { run: SandboxRun | null }) {
  const lines = [
    `[${formatTs(run?.createdAt)}] sandbox.run.start id=${run?.id ?? "—"}`,
    `[${formatTs(run?.createdAt)}] loading testset file=${run?.fileName ?? "—"}`,
    `[${formatTs(run?.createdAt)}] resolved ${run?.totalQuestions ?? 0} questions`,
    `[${formatTs(run?.createdAt)}] skill=${run?.skillVersion ?? "—"} baseline=${run?.comparisonVersion ?? "—"}`,
    `[${formatTs(run?.createdAt)}] worker pool ready · concurrency=4`,
    `[${formatTs(run?.createdAt)}] processed=${run?.processed ?? 0}/${run?.totalQuestions ?? 0}`,
    run?.status === "done"
      ? `[${formatTs(run.finishedAt)}] sandbox.run.complete status=done`
      : run?.status === "failed"
        ? `[${formatTs(run.finishedAt)}] sandbox.run.complete status=failed`
        : `[…] running`,
  ];

  return (
    <div className="rounded-xl border border-border bg-surface-1 shadow-sm overflow-hidden">
      <div className="px-4 py-2.5 border-b border-border-subtle bg-surface-2 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-success-text" />
        <span className="font-mono text-2xs tabular-nums text-text-muted">
          stdout · run.{run?.id ?? "—"}.log
        </span>
      </div>
      <pre
        className={cn(
          "px-4 py-4 text-xs font-mono text-text-secondary",
          "whitespace-pre-wrap leading-relaxed bg-surface-1",
        )}
      >
        {lines.join("\n")}
      </pre>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
 * Subcomponentes compartidos
 * ──────────────────────────────────────────────────────────── */

function KpiCard({
  label,
  value,
  sub,
  tone = "default",
}: {
  label: string;
  value: number | string;
  sub?: string;
  tone?: "default" | "success" | "warning" | "error" | "muted";
}) {
  const toneClass = {
    default: "text-text-primary",
    success: "text-success-text",
    warning: "text-warning-text",
    error: "text-error-text",
    muted: "text-text-muted",
  }[tone];
  return (
    <div className="rounded-xl border border-border bg-surface-1 p-5 shadow-sm">
      <div className="text-2xs uppercase tracking-wider text-text-muted mb-2">
        {label}
      </div>
      <div className={cn("font-mono font-bold tabular-nums text-3xl", toneClass)}>
        {value}
      </div>
      {sub && (
        <div className="font-mono text-2xs tabular-nums text-text-muted mt-1">
          {sub}
        </div>
      )}
    </div>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | number;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-3 min-w-0">
      <dt className="text-2xs uppercase tracking-wider text-text-muted w-28 flex-shrink-0">
        {label}
      </dt>
      <dd
        className={cn(
          "text-sm text-text-primary truncate",
          mono && "font-mono tabular-nums",
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function StatusBadge({ status }: { status: SandboxRun["status"] }) {
  const config = {
    done: {
      cls: "bg-success-bg border-success-border text-success-text",
      label: "Done",
      icon: <Check size={10} strokeWidth={3} />,
    },
    failed: {
      cls: "bg-error-bg border-error-border text-error-text",
      label: "Failed",
      icon: <X size={10} strokeWidth={3} />,
    },
    running: {
      cls: "bg-info-bg border-info-border text-info-text",
      label: "Running",
      icon: (
        <span
          className="w-1.5 h-1.5 rounded-full bg-current animate-pulse-soft"
          aria-hidden
        />
      ),
    },
    queued: {
      cls: "bg-state-closed-bg border-state-closed-border text-state-closed-text",
      label: "Queued",
      icon: null,
    },
  }[status];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full",
        "border text-2xs font-medium",
        config.cls,
      )}
    >
      {config.icon}
      {config.label}
    </span>
  );
}

function MatchPill({ pass }: { pass: boolean | null }) {
  if (pass === true) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-success-border bg-success-bg text-success-text text-2xs font-medium">
        <Check size={9} strokeWidth={3} />
        Match
      </span>
    );
  }
  if (pass === false) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-error-border bg-error-bg text-error-text text-2xs font-medium">
        <X size={9} strokeWidth={3} />
        Miss
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-border bg-surface-2 text-text-muted text-2xs font-medium">
      —
    </span>
  );
}

function formatTs(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toISOString().split("T")[1].slice(0, 8);
}
